import Phaser from 'phaser';
import { StateMachine, gridMove, frameMs, relativeDirection, oppositeDirection, weightedChoice } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyOptions, EnemyState } from 'tower/scenes/battle/enemy';
import { HitOptions } from 'tower/types';

const MOVE_SPEED = 20;

interface MummyOptions extends EnemyOptions {
  blockedSpaces?: { x: number; y: number }[];
}

interface Variant {
  idle: number;
  hp: number;
  touchDamage: number;
}

const VARIANTS: Record<string, Variant> = {
  blue: {
    idle: 416,
    hp: 10,
    touchDamage: 1,
  },
};

export default class Mummy extends Enemy {
  _skeleton = false;

  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantType = 'blue',
    public mummyOptions: MummyOptions = {},
  ) {
    const variant = VARIANTS[variantType];
    super(scene, x, y, 'enemies', variant.idle, 'mummy', {
      hp: variant.hp,
      touchDamage: variant.touchDamage,
      stunnable: true,
      stunDamage: false,
      turnable: false,
      knockback: false,
    });
    this.play(`mummy[${variantType}]`);
    this.body.setSize(12, 12);
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('moving', {
      moving: new MummyMovingState(scene, this),
    });
  }

  get variant() {
    return VARIANTS[this.variantType];
  }

  get skeleton() {
    return this._skeleton;
  }

  set skeleton(value: boolean) {
    this._skeleton = value;
    if (value) {
      this.hp = Math.min(this.hp ?? 2, 2);
      this.enemyOptions.knockback = true;
      this.play(`skeleton[${this.variantType}]`);
    } else {
      this.enemyOptions.knockback = false;
      this.play(`mummy[${this.variantType}]`);
    }
  }

  async hit(options: HitOptions) {
    if (options.weaponType === 'flame' && !this.skeleton) {
      this.skeleton = true;
      this.stateMachine.transition('hurt', options);
      this.scene.events.emit('mummyburned', this);
      return;
    }

    return super.hit(options);
  }

  static create(scene: BattleScene) {
    for (const variant of Object.keys(VARIANTS)) {
      const idle = VARIANTS[variant].idle;
      scene.anims.create({
        key: `mummy[${variant}]`,
        duration: frameMs(56),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle, idle + 1],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `skeleton[${variant}]`,
        duration: frameMs(56),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 2, idle + 3],
        }),
        repeat: -1,
      });
    }
  }
}

class MummyState extends EnemyState {
  declare enemy: Mummy;
}

class MummyMovingState extends MummyState {
  targetX!: number;
  targetY!: number;
  blockedFromPlayer = 0;
  reached = true;

  async handleEntered() {
    const { enemy, scene } = this;

    // If we did not reach our previous target, we got interrupted (e.g. hit) and should keep going.
    if (!this.reached) {
      return;
    }
    this.reached = false;

    const maze = scene.currentRoom.maze;
    const [sx, sy] = maze.worldXYToSpaceXY(enemy.x, enemy.y);

    // Filter out blocked spaces
    const openDirections = maze.getOpenDirectionsAtWorldXY(enemy.x, enemy.y).filter((d) => {
      const [nsx, nsy] = gridMove(sx, sy, d);
      if (enemy.mummyOptions.blockedSpaces?.some((blocked) => blocked.x === nsx && blocked.y === nsy)) {
        return false;
      }

      return true;
    });

    // Store a cooldown for if a wall separates us and the player
    const playerDirection = relativeDirection(enemy, scene.player);
    if (!openDirections.includes(playerDirection)) {
      this.blockedFromPlayer = 5;
    } else {
      this.blockedFromPlayer--;
    }

    const direction = weightedChoice(
      openDirections.map((d) => {
        // Prefer moving towards the player, if they are close and we aren't on cooldown
        if (
          d === playerDirection &&
          Phaser.Math.Distance.BetweenPoints(enemy, scene.player) < 24 * 4 &&
          this.blockedFromPlayer < 1
        ) {
          return [d, 2];
        }

        // Slightly prefer moving in the same direction
        if (d === enemy.direction) {
          return [d, 1.2];
        }

        // Prefer avoiding the direction we just came from
        if (d === oppositeDirection(enemy.direction)) {
          return [d, 0.3];
        }

        return [d, 1];
      }),
    );

    const [spaceX, spaceY] = maze.worldXYToSpaceXY(enemy.x, enemy.y);
    const [newSpaceX, newSpaceY] = gridMove(spaceX, spaceY, direction, 1);
    const [newWorldX, newWorldY] = maze.spaceXYToWorldXY(newSpaceX, newSpaceY);

    this.targetX = newWorldX;
    this.targetY = newWorldY;

    enemy.direction = direction;
    enemy.play(`${enemy.skeleton ? 'skeleton' : 'mummy'}[${enemy.variantType}]`, true);
  }

  update() {
    const { enemy, scene } = this;
    scene.physics.moveTo(enemy, this.targetX, this.targetY, MOVE_SPEED);
    if (Phaser.Math.Distance.Between(enemy.x, enemy.y, this.targetX, this.targetY) <= 1) {
      enemy.body.reset(this.targetX, this.targetY);
      this.reached = true;
      return this.transition('moving');
    }

    // If blocked, give up
    if (!enemy.body.blocked.none) {
      enemy.body.stop();
      this.reached = true;
      return this.transition('attack');
    }
  }
}
